<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import InputLabel from "@/Components/InputLabel.vue";
import TextInput from "@/Components/TextInput.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { computed, ref, onBeforeUnmount } from "vue";

const props = defineProps({
  tenant: { type: Object, default: null },
  readOnly: { type: Boolean, default: false },
});

const isEdit = !!props.tenant?.id;

// Para preview del archivo nuevo
const previewUrl = ref(null);
onBeforeUnmount(() => {
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value);
});

const existingLogoSrc = computed(() => {
  const t = props.tenant;
  return t?.logo_url || t?.logoUrl || t?.logo || null;
});

const form = useForm({
  _method: isEdit ? "PUT" : "POST",
  tenantName: props.tenant?.tenantName || "",
  app_name: props.tenant?.app_name || "",
  slug: props.tenant?.slug || "",

  // ✅ ahora logo es FILE
  logo: null,

  primary_domain: props.tenant?.primary_domain || "",
  testing_domain: props.tenant?.testing_domain || "",
  contact_address_line_1: props.tenant?.contact_address_line_1 || "",
  contact_address_line_2: props.tenant?.contact_address_line_2 || "",
  contact_country: props.tenant?.contact_country || "",
  contact_voice: props.tenant?.contact_voice || "",
  contact_fax: props.tenant?.contact_fax || "",
});

const logoSrc = computed(() => {
  if (previewUrl.value) return previewUrl.value;
  return existingLogoSrc.value;
});

const onLogoChange = (e) => {
  const file = e.target.files?.[0] || null;

  // limpiar preview anterior
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }

  form.logo = file;

  if (file) {
    previewUrl.value = URL.createObjectURL(file);
  }
};

const clearLogo = () => {
  if (previewUrl.value) {
    URL.revokeObjectURL(previewUrl.value);
    previewUrl.value = null;
  }
  form.logo = null;
  // ojo: esto NO borra el logo existente del tenant, solo "no sube uno nuevo".
  // Si querés un botón "Remove existing logo", lo hacemos con un flag aparte (ej: removeLogo=true).
};

const saveForm = () => {
  const routeName = isEdit
    ? route("admin.tenant-manager.update", { tenant: props.tenant.id })
    : route("admin.tenant-manager.store");

  if (isEdit) {
    form.post(routeName, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        window.location.href = route("admin.tenant-manager.index");
      },
    });
  } else {
    form.post(routeName, {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        window.location.href = route("admin.tenant-manager.index");
      },
    });
  }
};
</script>

<template>
  <AppLayout :title="isEdit ? 'Edit Tenant' : 'New Tenant'">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        {{ props.readOnly ? "View Tenant" : isEdit ? "Edit Tenant" : "Create Tenant" }}
      </h2>
    </template>

    <div class="max-w-2xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <a :href="route('admin.tenant-manager.index')">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="flex flex-col gap-4">
            <div>
              <InputLabel value="Name" :required="true" />
              <TextInput v-model="form.tenantName" class="w-full mt-1" :disabled="props.readOnly" />
              <div v-if="form.errors.tenantName" class="text-sm text-red-600 mt-1">
                {{ form.errors.tenantName }}
              </div>
            </div>

            <div>
              <InputLabel value="App Name" :required="true" />
              <TextInput v-model="form.app_name" class="w-full mt-1" :disabled="props.readOnly" />
              <div class="text-xs text-gray-500 mt-1">
                This name is used when <code>APP_NAME=tenant</code>.
              </div>
              <div v-if="form.errors.app_name" class="text-sm text-red-600 mt-1">
                {{ form.errors.app_name }}
              </div>
            </div>

            <div>
              <InputLabel value="Identifier" :required="true" />
              <TextInput v-model="form.slug" class="w-full mt-1" :disabled="props.readOnly || isEdit" />
              <div class="text-xs text-gray-500 mt-1" v-if="isEdit">
                Identifier cannot be changed after creation.
              </div>
              <div v-if="form.errors.slug" class="text-sm text-red-600 mt-1">
                {{ form.errors.slug }}
              </div>
            </div>

            <!-- ✅ Logo upload -->
            <div>
              <InputLabel value="Logo" />

              <div class="mt-1 flex items-start gap-4">
                <div class="w-40 h-24 rounded border bg-white flex items-center justify-center overflow-hidden">
                  <img v-if="logoSrc" :src="logoSrc" alt="Tenant logo preview" class="max-h-24 w-auto" />
                  <div v-else class="text-xs text-gray-400 px-2 text-center">
                    No logo
                  </div>
                </div>

                <div class="flex-1">
                  <input type="file" accept="image/*" class="block w-full text-sm" :disabled="props.readOnly"
                    @change="onLogoChange" />

                  <div class="text-xs text-gray-500 mt-1">
                    PNG/JPG recommended.
                  </div>

                  <div v-if="form.errors.logo" class="text-sm text-red-600 mt-1">
                    {{ form.errors.logo }}
                  </div>

                  <div class="mt-2 flex gap-2">
                    <SecondaryButton v-if="!props.readOnly" type="button" @click.prevent="clearLogo">
                      Clear new upload
                    </SecondaryButton>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <InputLabel value="Primary Domain" :required="true" />
              <TextInput v-model="form.primary_domain" class="w-full mt-1" :disabled="props.readOnly" />
              <div v-if="form.errors.primary_domain" class="text-sm text-red-600 mt-1">
                {{ form.errors.primary_domain }}
              </div>
            </div>

            <div>
              <InputLabel value="Testing Domain" />
              <TextInput v-model="form.testing_domain" class="w-full mt-1" :disabled="props.readOnly" />
              <div v-if="form.errors.testing_domain" class="text-sm text-red-600 mt-1">
                {{ form.errors.testing_domain }}
              </div>
            </div>

            <div class="pt-2 border-t border-gray-200">
              <InputLabel value="Quote Mail Contact Information" />
              <div class="text-xs text-gray-500 mt-1 mb-3">
                Used in quote request emails for this tenant. The Uniforms tenant always uses the built-in Premier Uniforms contact block in quote mails, even if these fields are filled in.
              </div>

              <div class="flex flex-col gap-4">
                <div>
                  <InputLabel value="Address line 1" />
                  <TextInput v-model="form.contact_address_line_1" class="w-full mt-1" :disabled="props.readOnly" />
                  <div v-if="form.errors.contact_address_line_1" class="text-sm text-red-600 mt-1">
                    {{ form.errors.contact_address_line_1 }}
                  </div>
                </div>

                <div>
                  <InputLabel value="Address line 2" />
                  <TextInput v-model="form.contact_address_line_2" class="w-full mt-1" :disabled="props.readOnly" />
                  <div class="text-xs text-gray-500 mt-1">City, state, and ZIP.</div>
                  <div v-if="form.errors.contact_address_line_2" class="text-sm text-red-600 mt-1">
                    {{ form.errors.contact_address_line_2 }}
                  </div>
                </div>

                <div>
                  <InputLabel value="Country" />
                  <TextInput v-model="form.contact_country" class="w-full mt-1" :disabled="props.readOnly" />
                  <div v-if="form.errors.contact_country" class="text-sm text-red-600 mt-1">
                    {{ form.errors.contact_country }}
                  </div>
                </div>

                <div>
                  <InputLabel value="Voice" />
                  <TextInput v-model="form.contact_voice" class="w-full mt-1" :disabled="props.readOnly" />
                  <div v-if="form.errors.contact_voice" class="text-sm text-red-600 mt-1">
                    {{ form.errors.contact_voice }}
                  </div>
                </div>

                <div>
                  <InputLabel value="Fax" />
                  <TextInput v-model="form.contact_fax" class="w-full mt-1" :disabled="props.readOnly" />
                  <div v-if="form.errors.contact_fax" class="text-sm text-red-600 mt-1">
                    {{ form.errors.contact_fax }}
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isEdit" class="pt-2 border-t border-gray-200">
              <InputLabel value="Navigation Settings" />
              <div class="mt-2 flex flex-wrap gap-2">
                <a :href="route('admin.tenant-manager.navigation.edit', { tenant: props.tenant.id })">
                  <SecondaryButton type="button">Edit Navigation Visibility</SecondaryButton>
                </a>
              </div>
              <div class="mt-4">
                <InputLabel value="Button Visibility Settings" />
              </div>
              <div class="mt-2 flex flex-wrap gap-2">
                <a :href="route('admin.tenant-manager.buttons.edit', { tenant: props.tenant.id })">
                  <SecondaryButton type="button">Edit Button Visibility</SecondaryButton>
                </a>
              </div>
            </div>

          </div>
        </template>

        <template #actions>
          <PrimaryButton v-if="!props.readOnly" @click.prevent="saveForm">
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
